import { ChevronDown } from 'lucide-react';
import { useMemo } from 'react';

import Menu from '@/components/Menu/Menu';
import { type MenuItemProps, MenuItemType } from '@/components/Menu/types';
import { CHART_FIELD } from '@/entities/chart/model/constants';
import {
  replaceSourceTerminology,
  useFeedbackTerminology,
} from '@/entities/workspace';
import {
  getFieldMenu,
  getParentFieldName,
} from '@/features/(charts)/chart-controller/hooks/use-field-config';
import { getChartFieldLabel } from '@/features/(charts)/chart-renderer/model/chart-maps';

const HIDDEN_MENU_KEYS = new Set<string | number>([
  CHART_FIELD.QUERY,
  'social-platform',
]);

const filterMenuItems = (
  items: MenuItemProps[],
  hiddenKeys: ReadonlySet<string | number>,
): MenuItemProps[] => {
  const filteredItems: MenuItemProps[] = items
    .flatMap((item) => {
      if (hiddenKeys.has(item.key ?? item.label)) {
        return [];
      }

      if (!item.subMenus) {
        return [item];
      }

      const subMenus: MenuItemProps[] = filterMenuItems(
        item.subMenus,
        hiddenKeys,
      );

      if (subMenus.length === 0) {
        return [];
      }

      return [
        {
          ...item,
          subMenus,
        },
      ];
    })
    .filter((item, index, array) => {
      if (item.type !== MenuItemType.DIVIDER) {
        return true;
      }

      const isFirst = index === 0;
      const isLast = index === array.length - 1;
      const prevIsDivider = array[index - 1]?.type === MenuItemType.DIVIDER;
      const nextIsDivider = array[index + 1]?.type === MenuItemType.DIVIDER;

      return !isFirst && !isLast && !prevIsDivider && !nextIsDivider;
    });

  return filteredItems;
};

const pipeText = (
  text: string,
  ...replacements: Array<(text: string) => string>
): string =>
  replacements.reduce((result, replace) => replace(result), text);

type Props = {
  targetFieldType: CHART_FIELD;
  onFieldChange: (field: CHART_FIELD) => void;
};

export default function DimensionFieldMenu({
  targetFieldType,
  onFieldChange,
}: Props) {
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const replaceSourceKeyword = (text: string) => replaceSourceTerminology(text);

  const fieldMenu = useMemo(() => {
    const replaceMenuTerminology = (text: string) =>
      pipeText(text, replaceFeedbackTerminology, (value) =>
        replaceSourceTerminology(value),
      );
    return filterMenuItems(
      getFieldMenu((field) => onFieldChange(field), replaceMenuTerminology),
      HIDDEN_MENU_KEYS,
    );
  }, [onFieldChange, replaceFeedbackTerminology]);

  const parentMenuName = pipeText(
    getParentFieldName(targetFieldType) ?? '',
    replaceSourceKeyword,
    replaceFeedbackTerminology,
  );

  const childMenuName = pipeText(
    getChartFieldLabel(targetFieldType),
    replaceSourceKeyword,
    replaceFeedbackTerminology,
  );

  return (
    <Menu
      modalWidth={356}
      triggerNode={
        <button className="bg-white w-full flex items-center justify-between px-3 h-8 border border-gray-400 rounded-md text-sm cursor-pointer hover:border-primary transition-all duration-200">
          {parentMenuName
            ? `${parentMenuName} / ${childMenuName}`
            : childMenuName}
          <ChevronDown className="size-4 text-gray-600" />
        </button>
      }
      options={fieldMenu}
    />
  );
}
